import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

const GaharJobEditDialog = ({ open, onClose, job, onSave }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState("");
  const [beds, setBeds] = useState("");
  const [loading, setLoading] = useState(false);

  const preventInvalidChars = (e) => {
    // Block 'e', 'E', '+', '-'
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Load data when job changes

  useEffect(() => {
    if (!open) {
      // Reset fields when dialog is closed
      setEmployees("");
      setBeds("");
    } else {
      setEmployees(job?.numberOfEmployees ?? 0);
      // Assuming 'workloadVolume' stores the Bed Count / Patient Load
      setBeds(job?.numberOfBeds ?? "");
    }
  }, [open]);

  const handleSaveInternal = async () => {
    setLoading(true);
    try {
      await onSave({
        numberOfEmployees: Number(employees),
        numberOfBeds: Number(beds), // Sending updated beds
        jobId: job.id,
      });
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t("user.scenarios.editJob")} : {job?.jobName}
      </DialogTitle>

      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
          {t("user.gahar.updateParametersDescription")}
        </Typography>

        {/* 1. Actual Employees */}
        <TextField
          label={t("user.gahar.numberOfEmployees")}
          type="number"
          fullWidth
          margin="normal"
          value={employees}
          // Update onChange to prevent pasting negative numbers
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || Number(val) >= 0) {
              setEmployees(val);
            }
          }}
          // 👇 BLOCK INVALID KEYS
          onKeyDown={preventInvalidChars}
          inputProps={{ min: 0 }}
        />

        {/* 2. Number of Beds */}
        <TextField
          label={t("user.gahar.numberOfBeds")}
          type="number"
          fullWidth
          margin="normal"
          value={beds}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || Number(val) >= 0) {
              setBeds(val);
            }
          }}
          // 👇 BLOCK INVALID KEYS
          onKeyDown={preventInvalidChars}
          inputProps={{ min: 0 }}
          helperText={t("user.gahar.bedsHelper")}
        />
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          {t("common.cancel")}
        </MDButton>
        <MDButton onClick={handleSaveInternal} variant="contained" color="info" disabled={loading}>
          {loading ? <CircularProgress size={20} color="inherit" /> : t("common.save")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

GaharJobEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  job: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default GaharJobEditDialog;
